import React from 'react';

import { APIRequest, PaginationParams, SearchRequest } from '../api/types';

import Logger from '../logger';

export const useSearch = <T>(
  request: (params?: SearchRequest) => APIRequest<T[]>,
  load = true,
  options: Partial<PaginationParams & { debounceMs?: number }> = {}
): {
  results: T[];
  loading: boolean;
  appliedQuery: string;
  /** True when the last request rejected. */
  failed: boolean;
  searchQuery: (phrase: string) => Promise<void>;
  /** Sends the current query again, e.g. once `failed`. */
  retry: () => void;
} => {
  const { offset = 0, debounceMs = 300 } = options;
  const limit = 'limit' in options ? options.limit : 10;

  const [results, setResults] = React.useState<T[]>([]);
  const [initialResults, setInitialResults] = React.useState<T[]>([]);
  const [requesting, setRequesting] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [requested, setRequested] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const currentRequestID = React.useRef(0);
  const activeQuery = React.useRef('');
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const searchQuery = async (phrase: string): Promise<void> => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setQuery(phrase);
    }, debounceMs);
  };

  // A failed first load leaves `requested` set, which is what stops an empty query asking again.
  const retry = (): void => setRequested(false);

  React.useEffect(() => {
    setRequested(false);
  }, [request]);

  React.useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    []
  );

  React.useEffect(() => {
    (async () => {
      // Tracks the query in play, so a rejection can tell "this search failed" from "the user has
      // since typed something else". A request-id bump can't: the effect re-runs on its own
      // `requested` write, which would strand the request still in flight.
      activeQuery.current = query;
      setFailed(false);

      if (query === '') {
        if (initialResults.length > 0) {
          setResults(initialResults);
          return;
        }
        if (!load || requested) {
          return;
        }
      }

      currentRequestID.current += 1;
      const requestID = currentRequestID.current;

      try {
        setRequesting(true);
        setRequested(true);
        const { data } = await request({ ...(limit !== undefined && { limit }), offset, query: query.trim() })();
        if (requestID === currentRequestID.current) {
          setResults(data);
          if (query.trim() === '') {
            setInitialResults(data);
          }
        }
      } catch (error) {
        if (requestID === currentRequestID.current && activeQuery.current === query) {
          setFailed(true);
        }
        Logger.warn('Invalid response from the search API', error);
      } finally {
        setRequesting(false);
      }
    })();
  }, [request, requested, load, limit, offset, query, initialResults]);

  return { results, loading: requesting, appliedQuery: query, failed, searchQuery, retry };
};
